'use client';

import { useSearchParams } from 'next/navigation';
import { animationSpring, animationTimingStyle, borderAnimation } from '@/styles/animation';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

interface AddToCartButtonProps {
	isLoading: boolean;
	onClick?: () => void;
	addToCartLabel: string;
	fullWidth?: boolean;
	state: 'none' | 'success';
	successLabel: string;
	hasMultipleVariants?: boolean;
	selectVariantBtnText?: string;
}

export default function AddToCartButton({
	isLoading,
	onClick,
	addToCartLabel,
	fullWidth,
	state,
	successLabel,
	hasMultipleVariants,
	selectVariantBtnText,
}: Readonly<AddToCartButtonProps>) {
	const successState = state === 'success';

	const searchParams = useSearchParams();
	const selectedVariant = searchParams.get('variant');

	const icon = successState ? (
		<TaskAltIcon
			sx={{
				animation: animationSpring,
				animationTimingFunction: animationTimingStyle,
			}}
		/>
	) : (
		<AddShoppingCartIcon />
	);

	const getButtonLabel = () => {
		if (!selectedVariant && hasMultipleVariants) return selectVariantBtnText;
		if (successState) return successLabel;
		return addToCartLabel;
	};

	return (
		<Button
			disabled={!selectedVariant && hasMultipleVariants}
			variant={isLoading || successState ? 'outlined' : 'contained'}
			startIcon={!selectedVariant && hasMultipleVariants ? false : icon}
			onClick={onClick}
			fullWidth={fullWidth}
			sx={{
				backgroundColor: isLoading || successState ? 'white' : 'primary.main',
				'.MuiButton-startIcon': {
					animation: successState ? animationSpring : 'none',
					animationTimingFunction: successState ? animationTimingStyle : 'none',
				},
				animation: isLoading ? `${borderAnimation} 1s infinite` : 'none',
			}}
			data-testid="add-to-cart-button"
		>
			<Box
				sx={{
					animation: successState ? animationSpring : 'none',
					animationTimingFunction: successState ? animationTimingStyle : 'none',
				}}
			>
				{getButtonLabel()}
			</Box>
		</Button>
	);
}
